import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CreditCardIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import api from '../services/api.ts';
import {
  formatCurrencyInput,
  parseCurrency,
  applyCurrencyMask,
} from '../utils/currency.ts';

interface DebtFormData {
  creditorName: string;
  description: string;
  originalAmount: number;
  currentAmount: number;
  interestRate: number;
  dueDate: string;
  status: 'active' | 'paid' | 'overdue' | 'renegotiated';
  category:
    | 'loan'
    | 'financing'
    | 'account_payable'
    | 'consortium'
    | 'real_estate'
    | 'vehicle'
    | 'other';
  paymentMethod?: string;
  installments?: number;
  currentInstallment?: number;
}

interface Debt extends DebtFormData {
  id: string;
  createdAt: string;
  updatedAt: string;
}

const EditDebt: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<DebtFormData>({
    creditorName: '',
    description: '',
    originalAmount: 0,
    currentAmount: 0,
    interestRate: 0,
    dueDate: new Date().toISOString().split('T')[0],
    status: 'active',
    category: 'other',
    paymentMethod: '',
    installments: 1,
    currentInstallment: 1,
  });

  const [originalAmountDisplay, setOriginalAmountDisplay] = useState('');
  const [currentAmountDisplay, setCurrentAmountDisplay] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const {
    data: debt,
    isLoading: isLoadingDebt,
    error: debtError,
  } = useQuery<Debt>(
    ['debt', id],
    async () => {
      const response = await api.get(`/debts/${id}`);
      return response.data;
    },
    {
      enabled: !!id,
    }
  );

  // Populate form when debt data is loaded
  useEffect(() => {
    if (debt) {
      setFormData({
        creditorName: debt.creditorName,
        description: debt.description,
        originalAmount: debt.originalAmount,
        currentAmount: debt.currentAmount,
        interestRate: debt.interestRate,
        dueDate: debt.dueDate.split('T')[0], // Format for date input
        status: debt.status,
        category: debt.category,
        paymentMethod: debt.paymentMethod || '',
        installments: debt.installments || 1,
        currentInstallment: debt.currentInstallment || 1,
      });

      // Exibir valores com máscara de moeda no input
      setOriginalAmountDisplay(formatCurrencyInput(debt.originalAmount));
      setCurrentAmountDisplay(formatCurrencyInput(debt.currentAmount));
    }
  }, [debt]);

  const updateDebtMutation = useMutation(
    async (data: DebtFormData) => {
      const response = await api.put(`/debts/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('debts');
        queryClient.invalidateQueries(['debt', id]);
        navigate(`/debts/${id}`);
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar dívida:', error);
        setErrors({ submit: 'Erro ao atualizar dívida. Tente novamente.' });
      },
    }
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'originalAmount') {
      const maskedValue = applyCurrencyMask(value);
      const numericValue = parseCurrency(maskedValue);
      setOriginalAmountDisplay(maskedValue);
      setFormData(prev => ({ ...prev, originalAmount: numericValue }));
    } else if (name === 'currentAmount') {
      const maskedValue = applyCurrencyMask(value);
      const numericValue = parseCurrency(maskedValue);
      setCurrentAmountDisplay(maskedValue);
      setFormData(prev => ({ ...prev, currentAmount: numericValue }));
    } else if (name === 'interestRate') {
      setFormData(prev => ({ ...prev, interestRate: Number(value) || 0 }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'installments' || name === 'currentInstallment' ? Number(value) : value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.creditorName.trim()) newErrors.creditorName = 'Nome do credor é obrigatório';
    // Descrição agora é opcional - removida validação obrigatória
    if (!formData.originalAmount || formData.originalAmount <= 0) {
      newErrors.originalAmount = 'Valor original deve ser maior que zero';
    }
    if (formData.currentAmount < 0) {
      newErrors.currentAmount = 'Saldo devedor não pode ser negativo';
    }
    if (formData.interestRate < 0 || formData.interestRate > 100) {
      newErrors.interestRate = 'Taxa de juros deve estar entre 0% e 100%';
    }
    if (!formData.dueDate) newErrors.dueDate = 'Data de vencimento é obrigatória';
    if (formData.installments && formData.installments < 1) {
      newErrors.installments = 'Número de parcelas deve ser maior que zero';
    }
    if (
      formData.currentInstallment &&
      formData.installments &&
      formData.currentInstallment > formData.installments
    ) {
      newErrors.currentInstallment = 'Parcela atual não pode ser maior que o total de parcelas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      updateDebtMutation.mutate(formData);
    }
  };

  if (isLoadingDebt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (debtError || !debt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Dívida não encontrada</h3>
          <p className="text-gray-500 mb-6">
            A dívida solicitada não foi encontrada ou não existe.
          </p>
          <button
            onClick={() => navigate('/debts')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Voltar para Dívidas
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header com botão de voltar */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/debts')}
              className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Voltar para Dívidas
            </button>
          </div>
          <div className="flex items-center">
            <CreditCardIcon className="h-8 w-8 text-blue-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Editar Dívida</h1>
              <p className="text-gray-600 mt-1">Atualize as informações da dívida</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          {/* Form Section */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Informações da Dívida</h2>
          </div>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{errors.submit}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label
                    htmlFor="creditorName"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Nome do Credor
                  </label>
                  <input
                    type="text"
                    name="creditorName"
                    id="creditorName"
                    value={formData.creditorName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Banco do Brasil, Nubank, Loja XYZ"
                  />
                  {errors.creditorName && (
                    <p className="text-red-500 text-sm mt-1">{errors.creditorName}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Categoria
                  </label>
                  <select
                    name="category"
                    id="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="loan">Empréstimo</option>
                    <option value="financing">Financiamento</option>
                    <option value="account_payable">Conta a Pagar</option>
                    <option value="consortium">Consórcio</option>
                    <option value="real_estate">Imóvel</option>
                    <option value="vehicle">Veículo</option>
                    <option value="other">Outros</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="originalAmount"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Valor Original (R$)
                  </label>
                  <input
                    type="text"
                    name="originalAmount"
                    id="originalAmount"
                    value={originalAmountDisplay}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="R$ 0,00"
                  />
                  {errors.originalAmount && (
                    <p className="text-red-500 text-sm mt-1">{errors.originalAmount}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="currentAmount"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Saldo Devedor (R$)
                  </label>
                  <input
                    type="text"
                    name="currentAmount"
                    id="currentAmount"
                    value={currentAmountDisplay}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="R$ 0,00"
                  />
                  {errors.currentAmount && (
                    <p className="text-red-500 text-sm mt-1">{errors.currentAmount}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="interestRate"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Taxa de Juros (% ao mês)
                  </label>
                  <input
                    type="number"
                    name="interestRate"
                    id="interestRate"
                    value={formData.interestRate}
                    onChange={handleInputChange}
                    min="0"
                    max="100"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00"
                  />
                  {errors.interestRate && (
                    <p className="text-red-500 text-sm mt-1">{errors.interestRate}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Data de Vencimento
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    id="dueDate"
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.dueDate && <p className="text-red-500 text-sm mt-1">{errors.dueDate}</p>}
                </div>

                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    id="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="active">Ativo</option>
                    <option value="paid">Pago</option>
                    <option value="overdue">Em Atraso</option>
                    <option value="renegotiated">Renegociado</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="paymentMethod"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Método de Pagamento
                  </label>
                  <input
                    type="text"
                    name="paymentMethod"
                    id="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Ex: Débito automático, Boleto, PIX"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Descrição <span className="text-gray-400 text-sm">(opcional)</span>
                </label>
                <textarea
                  name="description"
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Descreva a dívida (ex: Compra de móveis, Empréstimo pessoal, etc.)"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description}</p>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate(`/debts/${id}`)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateDebtMutation.isLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {updateDebtMutation.isLoading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditDebt;
